import {useMemo} from 'react';

import {envIsLocal} from '../../config';
import {useEntityTypeQueryResult} from '../../graphql/entityPage';
import {useProjectParams} from '../../pages/ProjectPage/useProjectParams';
import {useAccountPrivileges} from '../../util/accounts/pricing';
import {useRampFlagWorkspaceTemplates} from '../../util/rampFeatureFlags';
import {useAccountSelectorContext} from '../Search/SearchNav/AccountSelectorContextProvider';
import {AccountType} from '../Search/SearchNav/types';

/**
 * Workspace templates is currently a feature under development and is considered a premium feature.
 * We should only show the card if ramp flag is enabled and user is on an account that is on the enterprise plan.
 */
export const useIsWorkspaceTemplateEnabled = () => {
  const isWorkspaceTemplatesRampEnabled = useRampFlagWorkspaceTemplates();
  const isWorkspaceTemplateAccountPrivilegeEnabled =
    useIsWorkspaceTemplatePrivilegeEnabled();

  return useMemo(
    () => ({
      // This is the flag that determines if user should even see the feature.
      isWorkspaceTemplatesRampEnabled,
      // This is the flag that determines if the user is on an account that is on the enterprise plan.
      // If feature is enabled and user is not on the correct plan, we will show an upgrade UX.
      isWorkspaceTemplateAccountPrivilegeEnabled,
    }),
    [
      isWorkspaceTemplateAccountPrivilegeEnabled,
      isWorkspaceTemplatesRampEnabled,
    ]
  );
};

export const useIsUserSettingsWorkspaceTemplateDisplayed = () => {
  const {isWorkspaceTemplatesRampEnabled} = useIsWorkspaceTemplateEnabled();
  const {selectedAccount} = useAccountSelectorContext();

  if (envIsLocal) {
    return isWorkspaceTemplatesRampEnabled;
  }

  // Intentionally don't check for isWorkspaceTemplateAccountPrivilegeEnabled because we want to show
  // the upgrade message if the org is not on the enterprise plan.
  return (
    isWorkspaceTemplatesRampEnabled &&
    selectedAccount?.accountType !== AccountType.Personal
  );
};

export const useIsWorkspaceTemplateActionDisplayed = (): {
  isLoadingEntityType: boolean;
  isWorkspaceTemplateActionDisplayed: boolean;
} => {
  const {isWorkspaceTemplatesRampEnabled} = useIsWorkspaceTemplateEnabled();

  // If viewer is on any personal entity page, either one they own or one they don't own,
  // we should not show the workspace template action. Personal entities do not belong to
  // an org, but the feature requires an entityOrgId so we remove thie completely.
  const {entityName} = useProjectParams();
  const {entityType, loading} = useEntityTypeQueryResult({
    entityName,
    skip: !isWorkspaceTemplatesRampEnabled,
  });

  return {
    isLoadingEntityType: loading,
    // Intentionally don't check for isWorkspaceTemplateAccountPrivilegeEnabled because we want to show
    // the upgrade message if the org is not on the enterprise plan.
    isWorkspaceTemplateActionDisplayed:
      isWorkspaceTemplatesRampEnabled && entityType !== 'personal',
  };
};

const useIsWorkspaceTemplatePrivilegeEnabled = () => {
  const {selectedAccount} = useAccountSelectorContext();
  // Note: this hook skips the account privileges query if envIsLocal already
  const {privileges} = useAccountPrivileges(selectedAccount);

  if (envIsLocal) {
    // There's no concept of account on server and all server accounts are on the enterprise plan for now.
    return true;
  }

  return privileges.workspaceTemplatesEnabled;
};
